import React, { ReactNode, useEffect, useState } from "react";
import useCognitoHelper from "../hooks/useCognitoHelper";
import { CognitoUserSession, CognitoUser } from "amazon-cognito-identity-js";
import _ from "lodash";

export interface UserSessionInfo {
  claims: any;
  jwt: string;
  refreshToken: string;
}

export interface UserProviderContextInterface {
  userSession: UserSessionInfo | undefined;
  setUserSession: () => void;
  hasRole: (role: string) => boolean;
  user?: CognitoUser;
}

export interface UserProviderProps {
  children: ReactNode;
}

const UserContext = React.createContext<UserProviderContextInterface>({
  userSession: undefined,
  setUserSession: () => {
    return;
  },
  hasRole: (_role: string) => {
    return false;
  },
});

function getMinutesDifference(date1: Date, date2: Date): number {
  // Ensure the inputs are valid Date objects
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
    throw new Error("Both arguments must be Date objects.");
  }

  // Get the difference in milliseconds
  const differenceInMilliseconds = Math.abs(date1.getTime() - date2.getTime());

  // Convert the difference from milliseconds to minutes
  const differenceInMinutes = differenceInMilliseconds / (1000 * 60);

  return differenceInMinutes;
}

const UserProvider = ({ children }: UserProviderProps): JSX.Element => {
  const { Provider } = UserContext;
  const { getAuthenticatedUser } = useCognitoHelper();
  const [lastTokenRefresh, setLastTokenRefresh] = useState(new Date());
  const [userSessionInfo, setUserSessionInfo] = useState<UserSessionInfo>();
  const [getUser, setUser] = useState<CognitoUser>();

  useEffect(() => {
    setUserSession();
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFocus = () => {
    if (getMinutesDifference(new Date(), lastTokenRefresh) > 25) {
      setUserSession();
    }
  };

  function hasRole(role: string) {
    return userSessionInfo?.claims?.roles?.includes(role) || false;
  }

  function setUserSession() {
    const user = getAuthenticatedUser();
    user?.getSession((err: null, session: CognitoUserSession) => {
      if (err) {
        setUserSessionInfo(undefined);
        return;
      }
      const sessionClaims = session.getIdToken().payload;

      const claims = getSessionClaims(sessionClaims);
      const userSession = getUserSession(session, claims);

      setUserSessionInfo(userSession);
      setUser(user);
      setLastTokenRefresh(new Date());
    });
  }

  function getSessionClaims(sessionClaims: any) {
    let claims: any = { roles: [] };
    if (sessionClaims?.user) {
      const tmpData = JSON.parse(sessionClaims.user);
      claims = {
        user: _.pick(tmpData, ["firstName", "lastName", "email", "id"]),
        roles: tmpData.roles,
      };
    }
    return claims;
  }

  function getUserSession(session: CognitoUserSession, claims: any) {
    const userSession = {
      claims: claims,
      jwt: "Bearer " + session.getIdToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
    };
    console.log(userSession);
    return Object.create(userSession);
  }

  return (
    <Provider
      value={{
        userSession: userSessionInfo,
        setUserSession: setUserSession,
        hasRole: hasRole,
        user: getUser,
      }}
    >
      {children}
    </Provider>
  );
};

export { UserContext, UserProvider };
